import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Delete, History } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { useAMC } from '../context/AMCContext';
import { useState, useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';
import MerchantConfigDetail from './MerchantConfigDetail';
import { getAggregatorLogo } from '@/constants/icons';
import {
  useDeleteMerchantConfigMutation,
  useGetMerchantConfigsByAMCQuery,
  useCreateGatewayExclusionMutation,
  useDeleteGatewayExclusionMutation,
  useGetGatewayExclusionsQuery,
} from '@/services';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

export default function MerchantConfigList() {
  const { amcId, amc } = useAMC();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    data: merchantConfigs,
    isLoading,
    error,
  } = useGetMerchantConfigsByAMCQuery(amcId!, { skip: !amcId });
  const [deleteMerchantConfig] = useDeleteMerchantConfigMutation();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [chosenConfigId, setChosenConfigId] = useState<string | undefined>();

  // Exclusions state
  const { data: exclusionData, isLoading: isExclLoading } = useGetGatewayExclusionsQuery(
    { amcId: amcId ?? undefined },
    { skip: !amcId }
  );
  const [createExclusion, { isLoading: isCreatingExcl }] = useCreateGatewayExclusionMutation();
  const [deleteExclusion, { isLoading: isDeletingExcl }] = useDeleteGatewayExclusionMutation();

  const excludedIds = useMemo(() => new Set(exclusionData?.exclusions ?? []), [exclusionData]);

  const showAMCCreateOrEditDialog = (configId?: string) => {
    setChosenConfigId(configId);
    setCreateDialogOpen(true);
  };

  const closeCreateDialog = () => {
    setCreateDialogOpen(false);
    setChosenConfigId(undefined);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setChosenConfigId(undefined);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMerchantConfig(id).unwrap();
      enqueueSnackbar('Merchant configuration deleted successfully', { variant: 'success' });
      closeDeleteDialog();
    } catch (error: any) {
      enqueueSnackbar(error.data?.message || 'Failed to delete merchant configuration', {
        variant: 'error',
      });
    }
  };

  const handleServiceDown = async (id: string | undefined) => {
    if (!id) return;
    try {
      if (excludedIds.has(id)) {
        // Terminate early
        await deleteExclusion({ gatewayId: id, amcId: amcId ?? undefined }).unwrap();
        enqueueSnackbar('Temporary exclusion terminated', { variant: 'success' });
      } else {
        // 15 minutes hard down
        await createExclusion({
          gatewayId: id,
          ttlMs: 15 * 60 * 1000,
          amcId: amcId ?? undefined,
        }).unwrap();
        enqueueSnackbar('Gateway excluded for 15 minutes', { variant: 'success' });
      }
    } catch (error: any) {
      enqueueSnackbar(error.data?.message || 'Failed to update exclusion', { variant: 'error' });
    }
  };

  if (isLoading) {
    return <Typography>Loading...</Typography>;
  }

  if (error) {
    return <Typography color="error">Error loading payment aggregators</Typography>;
  }

  return (
    <Box p={3}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
          borderBottom: theme.borders.borderLight,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <Typography sx={{ fontWeight: 'bold', mb: isMobile ? 2 : 0 }}>
          Configured Payment Aggregators
        </Typography>
        <Button
          size="small"
          color="primary"
          disableElevation
          sx={{
            fontSize: '12px',
            borderRadius: '20px',
            width: isMobile ? '100%' : 'auto',
          }}
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => showAMCCreateOrEditDialog()}
        >
          Add Payment Aggregator
        </Button>
      </Box>
      {isMobile ? (
        <Box sx={{ mt: 2 }}>
          {merchantConfigs?.map((config) => (
            <Paper key={config.id} sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">{config.merchantId}</Typography>
                <Box>
                  <IconButton onClick={() => showAMCCreateOrEditDialog(config.id)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={() => {
                      setChosenConfigId(config.id);
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Delete fontSize="small" color="error" />
                  </IconButton>
                </Box>
              </Box>
              {amc?.isRTA && <Typography>Fund Code: {config.fundCode}</Typography>}
              {config.aggregator && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <img
                    src={getAggregatorLogo(config.aggregator)}
                    alt={config.aggregator}
                    style={{ width: 24, height: 24 }}
                  />
                  {config.aggregator}
                </Box>
              )}
            </Paper>
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Merchant ID</TableCell>
                {amc?.isRTA && <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Fund Code</TableCell>}
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Aggregator</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', py: 2 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading
                ? [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    {amc?.isRTA && (
                      <TableCell>
                        <Skeleton variant="text" />
                      </TableCell>
                    )}
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="circular" width={32} height={32} />
                      <Skeleton variant="circular" width={32} height={32} sx={{ ml: 1 }} />
                    </TableCell>
                  </TableRow>
                ))
                : merchantConfigs?.map((config) => {
                  const isExcluded = excludedIds.has(config.id ?? '');
                  return (
                    <TableRow key={config.id} hover>
                      <TableCell>{config.merchantId}</TableCell>
                      {amc?.isRTA && <TableCell>{config.fundCode}</TableCell>}
                      <TableCell>
                        {config.aggregator && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <img
                              src={getAggregatorLogo(config.aggregator)}
                              alt={config.aggregator}
                              style={{ width: 24, height: 24 }}
                            />
                            {config.aggregator}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip
                          title={
                            isExcluded
                              ? 'Cancel temporary exclusion'
                              : 'Service temporarily down (15 min)'
                          }
                        >
                          <span>
                            <IconButton
                              onClick={() => handleServiceDown(config.id)}
                              disabled={isExclLoading || isCreatingExcl || isDeletingExcl}
                            >
                              <HourglassEmptyIcon
                                fontSize="small"
                                color={isExcluded ? 'error' : 'warning'}
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <IconButton component={RouterLink} to={`${config.id}/history`}>
                          <History fontSize="small" />
                        </IconButton>
                        <IconButton onClick={() => showAMCCreateOrEditDialog(config.id)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            setChosenConfigId(config.id);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Delete fontSize="small" color="error" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this payment aggregator?</Typography>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={closeDeleteDialog}>
            Cancel
          </Button>
          <Button color="error" onClick={() => handleDelete(chosenConfigId!)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={createDialogOpen} onClose={closeCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {chosenConfigId ? 'Edit Payment Aggregator' : 'Add Payment Aggregator'}
        </DialogTitle>
        <DialogContent>
          {createDialogOpen && (
            <MerchantConfigDetail configId={chosenConfigId} amc={amc} onClose={closeCreateDialog} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
